import React, { useState } from 'react';
import { UserCog, X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../packages/auth-client';
import { getErrorMessage } from '../../packages/api-client';
import { formatDeletionDate } from '../../packages/api-client/account-deletion-api';

export function ImpersonationBanner() {
    const { isImpersonating, impersonationTarget, exitImpersonation } = useAuth();
    const [exiting, setExiting] = useState(false);
    if (!isImpersonating) return null;

    // exitImpersonation audits the exit, restores the operator's token and
    // clears the query cache; a full reload then starts every provider from
    // that identity, so nothing of the impersonated user is rendered next to
    // the operator's (mirrors the full-page load ImpersonatePage does on entry).
    const handleExit = async () => {
        setExiting(true);
        try {
            await exitImpersonation();
        } catch {
            // Reload regardless: exitImpersonation restores the operator
            // tokens before anything in it can fail, and the reload is what
            // drops the effective user's state.
        }
        window.location.assign('/admin/impersonate');
    };

    const tenant = impersonationTarget?.tenant_name ?? impersonationTarget?.tenant_id;

    return (
        <div role="region" aria-label="Impersonation" style={{
            background: 'var(--warn)', color: 'var(--on-warn)', padding: '0.5rem 1.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '0.5rem 1rem',
            fontSize: '0.85rem', fontWeight: 700, zIndex: 100, flexShrink: 0,
        }}>
            <span>
                <UserCog size={15} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }} />
                Acting as <strong>{impersonationTarget?.email ?? 'another user'}</strong>
                {tenant && <> in <strong>{tenant}</strong></>}
                {' '}— changes are recorded as this user and attributed to you.
            </span>
            <button
                type="button"
                onClick={handleExit}
                disabled={exiting}
                style={{
                    background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.25)',
                    borderRadius: 8, padding: '0.3rem 0.9rem', cursor: exiting ? 'default' : 'pointer',
                    fontWeight: 700, fontSize: '0.8rem', color: 'var(--on-warn)',
                    whiteSpace: 'nowrap',
                }}
            >
                <X size={14} aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: 4 }} />
                {exiting ? 'Exiting…' : 'Exit impersonation'}
            </button>
        </div>
    );
}

/**
 * Shown across the top of the app while the account is inside its 30-day
 * deletion window. It lives here rather than in profile settings because the
 * window expires whether or not someone who changed their mind goes looking.
 */
export function PendingDeletionBanner() {
    const { pendingDeletion, cancelDeletion } = useAuth();
    const [cancelling, setCancelling] = useState(false);
    if (!pendingDeletion) return null;

    const handleCancel = async () => {
        setCancelling(true);
        try {
            await cancelDeletion();
            toast.success('Your account will not be deleted.');
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div style={{
            background: 'var(--danger)', color: 'var(--on-danger)', padding: '0.5rem 1.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '0.5rem 1rem', fontSize: '0.85rem', fontWeight: 700, zIndex: 100, flexShrink: 0,
        }}>
            <span>
                <AlertTriangle size={15} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                This account is scheduled for deletion on{' '}
                <strong>{formatDeletionDate(pendingDeletion.scheduled_deletion_at)}</strong>.
            </span>
            <button
                onClick={handleCancel}
                disabled={cancelling}
                style={{
                    background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.25)',
                    borderRadius: 8, padding: '0.3rem 0.9rem',
                    cursor: cancelling ? 'default' : 'pointer',
                    fontWeight: 700, fontSize: '0.8rem', color: 'var(--on-danger)',
                    whiteSpace: 'nowrap',
                }}
            >
                {cancelling ? 'Cancelling…' : 'Keep my account'}
            </button>
        </div>
    );
}
